/* eslint-disable max-lines -- Pre-existing violation */
import {isFeatureEnabled} from 'feature-flags';
import {
  Button,
  Checkbox,
  CheckboxProps,
  ChoiceProps,
  DataStatus,
  Separator,
} from 'platform/components';
import {
  Box,
  getSize,
  getStepsFromPixels,
  Hide,
  HStack,
  Integer,
  Show,
  Text,
  VStack,
} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import styled, {css, useTheme} from 'styled-components';
import {match} from 'ts-pattern';

import {ReactNode, RefObject, useEffect, useMemo, useRef, useState} from 'react';

import {head, isNil, isNotEmpty} from 'ramda';
import {isNotNil, isString, round} from 'ramda-adjunct';

import {ROWS_PER_PAGE, getNextInfiniteScrollPage} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {
  EMPTY_PLACEHOLDER,
  Nullish,
  pushGtmEvent,
  suffixTestId,
  TestIdProps,
  useOnMount,
} from 'shared/web';

import {useIsElementNarrowerThan} from '../../hooks/useIsElementNarrowerThan';
import {useIsSourcingCardViewEnforced} from '../../hooks/useIsSourcingCardViewEnforced';
import {useSourcingPage} from '../../hooks/useSourcingPage';
import {SourcingType, useSourcingType} from '../../hooks/useSourcingType';
import {SourcingTableDispatch} from '../../store/sourcingTable/reducer';
import {Row} from '../../types/Row';
import {SourcingPage} from '../../types/SourcingPage';
import {SourcingTableQueryResult} from '../../types/SourcingTableQueryResult';
import {getSourcingVehicleId} from '../../utils/getSourcingVehicleId';
import {isAuctionVehicle} from '../../utils/isAuctionVehicle';
import {isUnifiedSourcingEnabled} from '../../utils/isUnifiedSourcingEnabled';
import {AdvanceAuctionView} from '../AdvanceAuctionView/AdvanceAuctionView';
import {AdvanceVehicleView} from '../AdvanceVehicleView/AdvanceVehicleView';
import {InfiniteScrollFooterStatus} from '../InfiniteScrollListener/components/InfiniteScrollFooterStatus';
import {InfiniteScrollListener} from '../InfiniteScrollListener/InfiniteScrollListener';
import {SourcingCebiaReportProvider} from '../SourcingCebiaReport/components/SourcingCebiaReportProvider';
import {SourcingListSkeleton} from '../SourcingListSkeleton/SourcingListSkeleton';
import {DataViewBulkActionDrawer} from './components/DataViewBulkActionDrawer';
import {LiveLastUpdateTime} from './components/LiveLastUpdateTime';
import {SourcingTableRealTimeSwitch} from './components/SourcingTableRealTimeSwitch';
import {SourcingTableRow} from './components/SourcingTableRow';
import {SourcingTableSkeleton} from './components/SourcingTableSkeleton';
import {SourcingTableSortChoice} from './components/SourcingTableSortChoice';
import {SourcingTableViewSettings} from './components/SourcingTableViewSettings';
import {SourcingViewChoice} from './components/SourcingViewChoice';
import {COMPACT_TABLE_GEOMETRY} from './constants/COMPACT_TABLE_GEOMETRY';
import {useHandleKeyboards} from './hooks/useHandleKeyboards';
import {SourcingTableState} from './types/SourcingTableState';
import {createRowElementId} from './utils/createRowElementId';
import {Column} from './utils/getSourcingTableColumns';
import {getSourcingTableListScrollState} from './utils/getSourcingTableListScrollState';
import {SourcingViewMode, sourcingTableSessionStorage} from './utils/sourcingTableSessionStorage';

interface TableProps extends TestIdProps {
  state: SourcingTableState;
  dispatch: SourcingTableDispatch;
  columns: Column[];
  sortByOptions: ChoiceProps<string>['options'];
  queryResult: SourcingTableQueryResult;
  isRealTimeEnabled?: boolean;
  /** Sort id to force when real-time mode is toggled on.
   * Classifieds passes CLASSIFIEDS_DEFAULT_ORDER_ID; Auctions omits it to keep the current sort. */
  realTimeOrderBy?: string;
  /** Serialized filter body + sort id; remounts the scroll container when either changes. */
  listQueryKey?: string;
  /** When true, leave the sort Choice empty (e.g. `?sort=` overrides the dropdown). */
  isSortByUnset?: boolean;
  /** Clear URL sort override when the user picks a dropdown option. */
  onClearUrlSort?: () => void;
  viewMode?: SourcingViewMode;
  onViewModeChange?: (mode: SourcingViewMode) => void;
  mapView?: ReactNode;
  /** Resolved sort fields of a user list, passed on to the export of a selection. */
  orderBy?: string[] | Nullish;
}

/**
 * Half the gap between two columns, in theme spacing units - the header row uses `spacing={2}`, so
 * half of it is one unit. A header label may use it, so a label that is slightly wider than its
 * column still reads in full; two neighbouring labels can at most meet, never overlap. Anything
 * longer than that is ellipsised by the label's own `noWrap`.
 */
const HEADER_CELL_BLEED = 1 as Integer;

/** Width, in theme spacing units, below which the compact table's columns no longer fit. */
function getCompactTableMinWidth(columns: Column[], scrollbarSteps: number) {
  const columnsWidth = columns.reduce(
    (sum, column) => sum + (column.width ?? COMPACT_TABLE_GEOMETRY.flexibleColumnMinWidth),
    0
  );
  // Checkbox column, the columns and the trailing space are separated by one gap each.
  const gapsCount = columns.length + 1;

  return (
    2 * COMPACT_TABLE_GEOMETRY.horizontalPadding +
    scrollbarSteps +
    COMPACT_TABLE_GEOMETRY.checkboxColumnWidth +
    columnsWidth +
    COMPACT_TABLE_GEOMETRY.trailingSpaceWidth +
    gapsCount * COMPACT_TABLE_GEOMETRY.columnSpacing
  );
}

const HEIGHT_OF_TABLE_HEADER_DETAILED_VIEW = 12;
const HEIGHT_OF_TABLE_HEADER_AND_FOOTER_DETAILED_VIEW = 28;
const HEIGHT_OF_TABLE_HEADER = 20;
const HEIGHT_OF_TABLE_HEADER_AND_FOOTER = 36;
const FILL_AVAILABLE_SPACE_PROPS = {flex: 1, minHeight: 0} as const;

function SourcingListLayoutAnalytics(props: {
  sourcingPage: SourcingPage;
  sourcingType: SourcingType;
  isDetailedView: boolean;
  tableRef: RefObject<HTMLDivElement | null>;
  compactTableMinWidth: number;
}) {
  useOnMount(() => {
    const tableWidth = props.tableRef.current?.clientWidth ?? 0;
    const isTableTooNarrow =
      tableWidth > 0 && (getStepsFromPixels(tableWidth) ?? 0) < props.compactTableMinWidth;
    const event = `sourcing_${props.sourcingType}_list_layout_viewed`;
    pushGtmEvent({
      event,
      event_id: event,
      params: {
        list_page: props.sourcingPage || 'all-offers',
        view: props.isDetailedView || isTableTooNarrow ? 'detail' : 'compact',
      },
    });
  });

  return null;
}

// eslint-disable-next-line max-lines-per-function, max-statements, complexity -- Pre-existing violation
export function SourcingTable(props: TableProps) {
  const formatNumber = useFormatNumber();
  const theme = useTheme();
  const sourcingPage = useSourcingPage();
  const sourcingType = useSourcingType();
  const [isDetailedViewSelection, setIsDetailedViewSelection] = useState(
    sourcingTableSessionStorage.getLastSelectedView()
  );
  const viewMode = props.viewMode ?? (isDetailedViewSelection ? 'cards' : 'table');
  const isMapView = viewMode === 'map' && isNotNil(props.mapView);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const scrollbarSteps = round(scrollbarWidth * theme.space.step);
  const tableRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isCardViewEnforcedByViewport = useIsSourcingCardViewEnforced();
  const compactTableMinWidth = getCompactTableMinWidth(props.columns, scrollbarSteps);
  const isTableTooNarrow = useIsElementNarrowerThan(tableRef, compactTableMinWidth);
  const isCardViewEnforced = isCardViewEnforcedByViewport || isTableTooNarrow;
  // Below the notebook breakpoint, or when the resizable preview leaves the panel narrower than the
  // compact columns need, the table does not fit, so the card view is forced. This is a render-time
  // override only - the stored user preference is left untouched.
  const isDetailedView = !isMapView && (isCardViewEnforced || viewMode === 'cards');
  const {queryResult} = props;

  // Calculate table scrollbar width to adjust padding of the header
  useEffect(
    function measureScrollbarWidth() {
      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer) {
        const scrollbarWidth = scrollContainer.offsetWidth - scrollContainer.clientWidth;
        setScrollbarWidth(scrollbarWidth);
      }
    },
    [queryResult.data]
  );

  const onSelectViewMode = (mode: SourcingViewMode) => {
    const event = `sourcing_${sourcingType}_view_selected`;
    pushGtmEvent({
      event,
      event_id: event,
      params: {
        view: match(mode)
          .with('table', () => 'compact')
          .with('cards', () => 'detail')
          .with('map', () => 'map')
          .exhaustive(),
      },
    });
    if (mode === 'cards') {
      props.dispatch({type: 'changeCheckedRows', rows: []});
    }
    if (props.onViewModeChange) {
      props.onViewModeChange(mode);
    } else {
      setIsDetailedViewSelection(mode === 'cards');
      sourcingTableSessionStorage.setLastSelectedView(mode === 'cards');
    }
  };

  useHandleKeyboards(
    props.state.selectedRow,
    queryResult.data,
    props.state.tableName,
    props.dispatch,
    !isMapView
  );

  const {page, selectedRow, tableName} = props.state;
  const {data} = queryResult;
  const {dispatch} = props;

  // Must stay in sync with sourcing/api.ts forceRefetch. See README §Infinite scroll.
  const {hasMoreRows, hiddenVehiclesCount, showTerminalFooter, showHiddenVehiclesMessage} =
    getSourcingTableListScrollState({
      dataLength: data.length,
      count: queryResult.count,
      isEndReached: queryResult.isEndReached,
    });
  const nextPage = getNextInfiniteScrollPage(data.length, queryResult.scrollAttemptedOffset);

  // Set last visited page if data are still cached, otherwise set first page
  useEffect(
    function restoreLastVisitedPage() {
      if (isNil(page) && isNotEmpty(data)) {
        const savedPage = sourcingTableSessionStorage.getLastVisitedPage(tableName);
        dispatch({type: 'changePage', page: savedPage ?? 1});
      }
    },
    [data, dispatch, page, tableName]
  );

  // Select last visited vehicle and then scroll on it
  useEffect(
    function selectLastVisitedVehicle() {
      if (isNil(selectedRow) && isNotEmpty(data)) {
        const lastVisitedVehicleId = sourcingTableSessionStorage.getLastVisitedVehicleId(tableName);
        const lastVisitedVehicle = data.find(
          (item) => lastVisitedVehicleId === item.sourcingVehicle.adId
        );
        const rowToSelect = lastVisitedVehicle ?? head(data);
        if (isNotNil(rowToSelect)) {
          dispatch({type: 'selectRow', row: rowToSelect});
        }

        if (lastVisitedVehicle) {
          window.document
            .getElementById(createRowElementId(lastVisitedVehicle))
            ?.scrollIntoView({block: 'nearest'});
        }
      }
    },
    [data, dispatch, selectedRow, tableName]
  );

  // Select first row if selected row is put to hidden list
  useEffect(
    function selectFirstRowWhenSelectedHidden() {
      if (
        isNotNil(selectedRow) &&
        !data.some((row) => row.sourcingVehicle.adId === selectedRow.sourcingVehicle.adId)
      ) {
        const firstRow = head(data);
        if (isNotNil(firstRow)) {
          dispatch({type: 'selectRow', row: firstRow});
        }
      }
    },
    [data, dispatch, selectedRow]
  );

  const dataByAdId = useMemo(
    () =>
      new Map(
        data
          .filter((row) => isNotNil(row.sourcingVehicle?.adId))
          .map((row) => [row.sourcingVehicle.adId, row])
      ),
    [data]
  );

  // Checked rows that are still in the loaded dataset, mapped to their current data (a checked
  // row held in state goes stale after e.g. a currency switch). Rows that dropped out of the
  // list - after the filter criteria changed, for instance - are excluded here, so bulk action
  // counts and the select-all state always match what the user can see.
  const checkedRows = props.state.checkedRows
    .map((checkedRow) => dataByAdId.get(checkedRow.sourcingVehicle?.adId))
    .filter(isNotNil);
  const checkedRowIds = new Set(checkedRows.map((row) => row.sourcingVehicle?.adId));

  const handleRowCheckbox =
    (row: Row): NonNullable<CheckboxProps['onChange']> =>
    (value) => {
      if (value) {
        props.dispatch({type: 'changeCheckedRows', rows: [...checkedRows, row]});
      } else {
        props.dispatch({
          type: 'changeCheckedRows',
          rows: checkedRows.filter(
            (row1) => row1.sourcingVehicle?.adId !== row.sourcingVehicle?.adId
          ),
        });
      }
    };

  const onUncheckCheckedRows = (rowIds: string[]) => {
    props.dispatch({
      type: 'changeCheckedRows',
      rows: checkedRows.filter((row1) => !rowIds.includes(getSourcingVehicleId(row1) ?? '')),
    });
  };

  const onSetLastVisitedVehicle = (adId: string) => {
    sourcingTableSessionStorage.setLastVisitedVehicleId(props.state.tableName, adId);
  };

  const onSelectRow = (row: Row) => {
    const event = `sourcing_${sourcingType}_result_selected`;
    pushGtmEvent({
      event,
      event_id: event,
    });
    props.dispatch({type: 'selectRow', row});
    const adId = getSourcingVehicleId(row);
    if (isNotNil(adId)) {
      onSetLastVisitedVehicle(adId);
    }
  };

  const onChangeRealTime = (isRealTime: boolean) => {
    const event = `sourcing_${sourcingType}_realtime_${isRealTime ? 'enabled' : 'disabled'}`;
    pushGtmEvent({event, event_id: event});
    props.dispatch({
      type: 'setRealTime',
      isRealTime,
      ...(props.realTimeOrderBy ? {orderBy: props.realTimeOrderBy} : {}),
    });
  };

  const onChangeSortBy: ChoiceProps<string>['onChange'] = (value) => {
    if (!isString(value)) {
      return;
    }
    const event = `sourcing_${sourcingType}_sort_changed`;
    pushGtmEvent({
      event,
      event_id: event,
    });
    props.onClearUrlSort?.();
    props.dispatch({
      type: 'sort',
      column: value,
      keepRealTime: !props.realTimeOrderBy,
    });
  };

  const sortByValue = props.isSortByUnset ? null : props.state.orderBy;
  const resultCountLabel = isNotNil(queryResult.count)
    ? i18n.t('general.labels.result', {
        count: queryResult.count,
        formattedCount: formatNumber(queryResult.count),
      })
    : EMPTY_PLACEHOLDER;

  const onRetry = () => {
    props.dispatch({type: 'resetData'});
    queryResult.onRetry();
  };

  // Card rows carry no checkbox, so a selection made in the compact table can be neither
  // inspected nor changed once the card view is enforced - offering bulk actions on it would let
  // the user buy or hide rows they cannot see. The selection is kept, not cleared, so widening
  // the window back above the breakpoint restores it.
  const isFooterVisible = isNotEmpty(checkedRows) && !isCardViewEnforced;
  const headerAndFooterHeight = match<[boolean, boolean], Integer>([
    isDetailedView,
    isFooterVisible,
  ])
    .with([true, true], () => HEIGHT_OF_TABLE_HEADER_AND_FOOTER_DETAILED_VIEW)
    .with([true, false], () => HEIGHT_OF_TABLE_HEADER_DETAILED_VIEW)
    .with([false, true], () => HEIGHT_OF_TABLE_HEADER_AND_FOOTER)
    .with([false, false], () => HEIGHT_OF_TABLE_HEADER)
    .exhaustive();

  const isCheckedAll =
    isNotEmpty(data) && data.every((row) => checkedRowIds.has(row.sourcingVehicle?.adId));

  const isLoadingMore =
    queryResult.isFetchingNextPage ?? (queryResult.isFetching && (props.state.page ?? 1) > 1);
  const shouldShowTerminalFooter = showTerminalFooter && !isLoadingMore && isNil(queryResult.error);
  const shouldShowManualLoadMore = hasMoreRows && !isLoadingMore && isNil(queryResult.error);
  const isRealTimeEnabled =
    props.isRealTimeEnabled && isFeatureEnabled(featureFlags.SOURCING_REAL_TIME_ADVERTISEMENT);
  const realTimeTooltip =
    sourcingType === SourcingType.AUCTIONS
      ? i18n.t('entity.sourcingTable.labels.realTimeAuctionsTooltip')
      : i18n.t('entity.sourcingTable.labels.realTimeTooltip');
  const enhancedSurfaces = isUnifiedSourcingEnabled();
  const fillAvailableSpaceProps = enhancedSurfaces ? FILL_AVAILABLE_SPACE_PROPS : {};
  const hasResultCount = isNotNil(queryResult.count);
  const hasViewChoice = isNotNil(props.mapView);
  // Sort and last update describe the list, so the map view leaves only the real-time switch.
  const hasViewSettings = !isMapView || isRealTimeEnabled;

  const table = (
    <SourcingTableRoot
      ref={tableRef}
      $hasUnifiedSurfaces={enhancedSurfaces}
      $isDetailedView={isDetailedView}
    >
      <SourcingListLayoutAnalytics
        key={`${sourcingType}:${sourcingPage}`}
        sourcingPage={sourcingPage}
        sourcingType={sourcingType}
        isDetailedView={isDetailedView}
        tableRef={tableRef}
        compactTableMinWidth={compactTableMinWidth}
      />
      <Box
        padding={2}
        backgroundColor={enhancedSurfaces ? 'general.white' : undefined}
        borderRadius={enhancedSurfaces ? 'small' : undefined}
        boxShadow={enhancedSurfaces ? 'elevation_1' : undefined}
      >
        {/* The full toolbar needs the width of the compact table. While the card view is enforced
            (small viewport, or a panel too narrow for the table) the compact toolbar below takes
            over - it also drops the table choice, which could not change the layout anyway. */}
        <Hide when={isCardViewEnforced}>
          <HStack
            align="center"
            justify={isRealTimeEnabled ? 'space-between' : 'flex-end'}
            spacing={2}
          >
            <Show when={isRealTimeEnabled}>
              <Box paddingLeft={2}>
                <SourcingTableRealTimeSwitch
                  value={props.state.isRealTime}
                  tooltip={realTimeTooltip}
                  onChange={onChangeRealTime}
                />
              </Box>
            </Show>
            <HStack spacing={2}>
              <Show when={!isMapView}>
                <HStack align="center" spacing={2}>
                  <Text size="xSmall" color="secondary">
                    {i18n.t('general.actions.sortBy')}
                  </Text>
                  <SourcingTableSortChoice
                    value={sortByValue}
                    options={props.sortByOptions}
                    onChange={onChangeSortBy}
                    data-testid={props['data-testid']}
                  />
                </HStack>
              </Show>
              {/* Separators go only between two visible groups - never at the edge of this group,
                  where it meets the flexible gap after the real-time switch. */}
              <Show when={hasResultCount && !isMapView}>
                <Separator orientation="vertical" spacing={0} />
              </Show>
              <Show when={hasResultCount}>
                <HStack align="center">
                  <Text size="xSmall" color="secondary">
                    {resultCountLabel}
                  </Text>
                </HStack>
              </Show>
              <Show when={!isMapView}>
                <LiveLastUpdateTime
                  lastUpdateTime={queryResult.lastUpdateTime}
                  isLiveUpdateEnabled={props.state.isRealTime && isRealTimeEnabled}
                  isPolling={queryResult.isPolling}
                />
              </Show>
              <Show when={!isMapView || hasResultCount}>
                <Separator orientation="vertical" spacing={0} />
              </Show>
              <HStack spacing={0} align="center" role="group">
                <SourcingViewChoice
                  icon="action/view_list"
                  label={i18n.t('entity.sourcingTable.labels.tableView')}
                  isActive={viewMode === 'table'}
                  onClick={() => onSelectViewMode('table')}
                  data-testid={suffixTestId('selectTableView', props)}
                />
                <SourcingViewChoice
                  icon="action/view_stream"
                  label={i18n.t('entity.sourcingTable.labels.detailedView')}
                  isActive={isDetailedView}
                  onClick={() => onSelectViewMode('cards')}
                  data-testid={suffixTestId('selectCardsView', props)}
                />
                <Show when={hasViewChoice}>
                  <SourcingViewChoice
                    icon="maps/place"
                    label={i18n.t('page.sourcing.map.view')}
                    isActive={isMapView}
                    onClick={() => onSelectViewMode('map')}
                    data-testid={suffixTestId('selectMapView', props)}
                  />
                </Show>
              </HStack>
            </HStack>
          </HStack>
        </Hide>

        {/* Kept to a single row so the height reserved for the table chrome stays correct. The row
            stretches its items, so the vertical separators get the full row height. */}
        <Show when={isCardViewEnforced}>
          <HStack justify="flex-end" spacing={2}>
            <Show when={hasResultCount}>
              <HStack align="center">
                <Text size="xSmall" color="secondary">
                  {resultCountLabel}
                </Text>
              </HStack>
            </Show>
            <Show when={hasResultCount && hasViewChoice}>
              <Separator orientation="vertical" spacing={0} />
            </Show>
            <Show when={hasViewChoice}>
              <HStack spacing={0} align="center" role="group">
                <SourcingViewChoice
                  icon="action/view_stream"
                  label={i18n.t('entity.sourcingTable.labels.detailedView')}
                  isActive={!isMapView}
                  onClick={() => onSelectViewMode('cards')}
                  data-testid={suffixTestId('selectCardsView', props)}
                />
                <SourcingViewChoice
                  icon="maps/place"
                  label={i18n.t('page.sourcing.map.view')}
                  isActive={isMapView}
                  onClick={() => onSelectViewMode('map')}
                  data-testid={suffixTestId('selectMapView', props)}
                />
              </HStack>
            </Show>
            <Show when={hasViewSettings && (hasResultCount || hasViewChoice)}>
              <Separator orientation="vertical" spacing={0} />
            </Show>
            <Show when={hasViewSettings}>
              <SourcingTableViewSettings
                isRealTimeEnabled={isRealTimeEnabled}
                isRealTime={props.state.isRealTime}
                realTimeTooltip={realTimeTooltip}
                onChangeRealTime={onChangeRealTime}
                hasListSettings={!isMapView}
                lastUpdateTime={queryResult.lastUpdateTime}
                isPolling={queryResult.isPolling}
                sortByValue={sortByValue}
                sortByOptions={props.sortByOptions}
                onChangeSortBy={onChangeSortBy}
                data-testid={props['data-testid']}
              />
            </Show>
          </HStack>
        </Show>
      </Box>
      <Separator spacing={0} />
      <Show when={isMapView}>
        <Box
          height={enhancedSurfaces ? undefined : `calc(100% - ${getSize(12)})`}
          {...fillAvailableSpaceProps}
          position="relative"
        >
          {props.mapView}
        </Box>
      </Show>
      <Hide when={isMapView}>
        <Hide when={isDetailedView}>
          <Box
            paddingLeft={COMPACT_TABLE_GEOMETRY.horizontalPadding}
            paddingRight={(COMPACT_TABLE_GEOMETRY.horizontalPadding + scrollbarSteps) as Integer}
            paddingVertical={2}
          >
            <HStack spacing={COMPACT_TABLE_GEOMETRY.columnSpacing} align="center">
              <Box width={COMPACT_TABLE_GEOMETRY.checkboxColumnWidth}>
                <Checkbox
                  value={isCheckedAll}
                  data-testid={suffixTestId('selectAllCheckbox', props)}
                  onChange={(value) =>
                    props.dispatch({type: 'changeCheckedRows', rows: value ? data : []})
                  }
                />
              </Box>
              {props.columns.map((column) => (
                <Box
                  key={column.id}
                  width={column.width}
                  minWidth={isNil(column.width) ? COMPACT_TABLE_GEOMETRY.flexibleColumnMinWidth : 0}
                  flex={isNotNil(column.width) ? undefined : 1}
                >
                  <div
                    // eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
                    css={css`
                      margin: 0 calc(-1 * ${theme.getSize(HEADER_CELL_BLEED)});
                      overflow: hidden;
                    `}
                  >
                    {column.Header}
                  </div>
                </Box>
              ))}
              <Box width={COMPACT_TABLE_GEOMETRY.trailingSpaceWidth} />
            </HStack>
          </Box>
          <Separator spacing={0} />
        </Hide>
        <DataStatus
          isError={
            isNotNil(queryResult.error) && (data.length === 0 || isNil(queryResult.onLoadMore))
          }
          isLoading={!enhancedSurfaces && queryResult.isLoading}
          minHeight={50}
          grow={enhancedSurfaces ? 1 : undefined}
          errorAction={{
            title: i18n.t('general.actions.retry'),
            variant: 'secondary',
            onClick: onRetry,
            isLoading: queryResult.isFetching,
          }}
        >
          {enhancedSurfaces && queryResult.isLoading ? (
            <Box {...fillAvailableSpaceProps} overflow="hidden">
              <Show when={isDetailedView}>
                <SourcingListSkeleton />
              </Show>
              <Hide when={isDetailedView}>
                <SourcingTableSkeleton columns={props.columns} />
              </Hide>
            </Box>
          ) : (
            <>
              <Box
                height={
                  enhancedSurfaces ? undefined : `calc(100% - ${getSize(headerAndFooterHeight)})`
                }
                {...fillAvailableSpaceProps}
                position="relative"
              >
                <InfiniteScrollListener
                  key={`infinite-scroll-listener-${props.listQueryKey}`}
                  scrollContainerRef={scrollContainerRef}
                  isFetching={queryResult.isFetching}
                  isEnabled={hasMoreRows && isNil(queryResult.error)}
                  onLoadMore={
                    queryResult.onLoadMore ??
                    (() => props.dispatch({type: 'changePage', page: nextPage}))
                  }
                />
                <div
                  key={`scroll-container-${props.listQueryKey}`}
                  css={css`
                    height: 100%;
                    overflow-y: scroll;
                  `}
                  ref={scrollContainerRef}
                >
                  <Box
                    padding={enhancedSurfaces && isDetailedView ? 1 : 0}
                    paddingTop={enhancedSurfaces && isDetailedView ? 3 : 0}
                  >
                    <VStack>
                      {data.map((row, index) => (
                        <div key={row.sourcingVehicle?.adId} id={row.sourcingVehicle?.adId}>
                          {!isDetailedView ? (
                            <>
                              <SourcingTableRow
                                hasUnifiedSurfaces={enhancedSurfaces}
                                key={row.sourcingVehicle?.adId}
                                onClick={() => onSelectRow(row)}
                                index={index}
                                columns={props.columns}
                                row={row}
                                isExpanded={props.state.expandedRowIds.includes(
                                  row.sourcingVehicle?.adId
                                )}
                                onExpandedChange={(row, isExpanded) =>
                                  props.dispatch({type: 'expandRow', row, isExpanded})
                                }
                                isSelected={
                                  isNotNil(props.state.selectedRow) &&
                                  props.state.selectedRow.sourcingVehicle?.adId ===
                                    row.sourcingVehicle?.adId
                                }
                                isChecked={checkedRowIds.has(row.sourcingVehicle?.adId)}
                                onCheck={handleRowCheckbox(row)}
                                setLastVisitedVehicle={onSetLastVisitedVehicle}
                              />
                              <Show when={index < data.length - 1}>
                                <Separator orientation="horizontal" spacing={0} />
                              </Show>
                            </>
                          ) : (
                            <>
                              {sourcingType === SourcingType.AUCTIONS && isAuctionVehicle(row) ? (
                                <AdvanceAuctionView
                                  index={index}
                                  key={row.sourcingVehicle?.adId}
                                  isLastClickedRow={
                                    isNotNil(props.state.selectedRow) &&
                                    props.state.selectedRow.sourcingVehicle?.adId ===
                                      row.sourcingVehicle?.adId
                                  }
                                  onRowClick={() => onSelectRow(row)}
                                  vehicle={{...row, shortcomings: [], highlights: []}}
                                  setLastVisitedVehicle={onSetLastVisitedVehicle}
                                  data-testid={suffixTestId('advanceVehicleView', props)}
                                />
                              ) : (
                                <AdvanceVehicleView
                                  index={index}
                                  key={row.sourcingVehicle?.adId}
                                  isLastClickedRow={
                                    isNotNil(props.state.selectedRow) &&
                                    props.state.selectedRow.sourcingVehicle?.adId ===
                                      row.sourcingVehicle?.adId
                                  }
                                  onRowClick={() => onSelectRow(row)}
                                  vehicle={{...row, shortcomings: [], highlights: []}}
                                  setLastVisitedVehicle={onSetLastVisitedVehicle}
                                  data-testid={suffixTestId('advanceVehicleView', props)}
                                />
                              )}
                              <Box
                                height={enhancedSurfaces ? 3 : 2}
                                backgroundColor={
                                  enhancedSurfaces ? undefined : 'palettes.neutral.10.100'
                                }
                              />
                            </>
                          )}
                        </div>
                      ))}
                      <HStack height={16} align="center" justify="center">
                        <InfiniteScrollFooterStatus
                          showRetry={
                            isNotNil(queryResult.error) &&
                            data.length > 0 &&
                            isNotNil(queryResult.onLoadMore)
                          }
                          showSpinner={isLoadingMore && isNil(queryResult.error)}
                          showComplete={shouldShowTerminalFooter}
                          isFetching={queryResult.isFetching}
                          onRetry={queryResult.onRetry}
                          completeText={
                            showHiddenVehiclesMessage
                              ? i18n.t(
                                  'entity.sourcingTable.labels.allVisibleResultsLoadedWithHidden',
                                  {
                                    count: hiddenVehiclesCount,
                                    formattedCount: formatNumber(hiddenVehiclesCount),
                                  }
                                )
                              : i18n.t('entity.sourcingTable.labels.allResultsLoaded')
                          }
                          compact
                          data-testid={props['data-testid']}
                        />
                        <Show when={shouldShowManualLoadMore}>
                          <Button
                            title={i18n.t('entity.sourcingTable.labels.loadMore', {
                              count: ROWS_PER_PAGE,
                            })}
                            variant="secondary"
                            onClick={() => {
                              const event = `sourcing_${sourcingType}_results_load_requested`;
                              pushGtmEvent({
                                event,
                                event_id: event,
                              });
                              if (isNotNil(queryResult.onLoadMore)) {
                                queryResult.onLoadMore();
                              } else {
                                props.dispatch({type: 'changePage', page: nextPage});
                              }
                            }}
                            data-testid={suffixTestId('loadMoreButton', props)}
                          />
                        </Show>
                      </HStack>
                    </VStack>
                  </Box>
                </div>
              </Box>
              <Show when={isFooterVisible}>
                <Box
                  padding={4}
                  minHeight={16}
                  height={enhancedSurfaces ? undefined : 16}
                  flexShrink={enhancedSurfaces ? 0 : undefined}
                  backgroundColor="general.white"
                  borderTop="1px solid"
                  borderColor="general.separator"
                >
                  <VStack height={enhancedSurfaces ? undefined : '100%'} justify="center">
                    <Box height={enhancedSurfaces ? undefined : '100%'}>
                      <DataViewBulkActionDrawer
                        selectedRows={checkedRows}
                        onUnselectSelectedRows={onUncheckCheckedRows}
                        hasUnifiedSurfaces={enhancedSurfaces}
                        orderBy={props.orderBy}
                      />
                    </Box>
                  </VStack>
                </Box>
              </Show>
            </>
          )}
        </DataStatus>
      </Hide>
    </SourcingTableRoot>
  );

  return (
    <SourcingCebiaReportProvider
      externalIds={data.map((row) => row.sourcingVehicle.adId)}
      isEnabled={sourcingType === SourcingType.CLASSIFIEDS}
    >
      {table}
    </SourcingCebiaReportProvider>
  );
}

const SourcingTableRoot = styled.div<{$hasUnifiedSurfaces: boolean; $isDetailedView: boolean}>`
  height: 100%;
  overflow: hidden;
  background-color: ${({$hasUnifiedSurfaces, $isDetailedView, theme}) =>
    $hasUnifiedSurfaces && $isDetailedView
      ? theme.colors.palettes.neutral[20][100]
      : theme.colors.general.white};
  border-radius: ${({$hasUnifiedSurfaces, theme}) =>
    $hasUnifiedSurfaces ? theme.radii.small : theme.radii.xSmall};
  box-shadow: ${({$hasUnifiedSurfaces, $isDetailedView, theme}) =>
    $hasUnifiedSurfaces && $isDetailedView ? 'none' : theme.shadows.elevation_1};
  ${({$hasUnifiedSurfaces}) =>
    $hasUnifiedSurfaces &&
    css`
      display: flex;
      flex-direction: column;
    `}
`;
